import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiAI, doctorId, drName } from "../apiUrl";

/* ===== Memoria de chat: un session_id por sesión del navegador, para que el
   agente pueda resolver "ella / sus / ese paciente". ===== */
function _newSessionId() {
  try {
    return crypto.randomUUID();
  } catch {
    return "sess-" + Date.now() + "-" + Math.random().toString(36).slice(2);
  }
}
let SESSION_ID = _newSessionId();
export function resetChatSession() {
  SESSION_ID = _newSessionId();
}

/* ===== Detecta link de Excel en distintos formatos de payload ===== */
function extractExcelDownload(payload) {
  // Prioridad: URL directa
  const direct =
    payload?.download_url ||
    payload?.excel_url ||
    payload?.file_url ||
    payload?.url;

  if (typeof direct === "string" && direct.trim()) {
    return { href: direct.trim(), filename: undefined };
  }

  // Excel path plano (servido estático) o remoto
  const pathFromRoot =
    payload?.excel_path ||
    payload?.data?.excel_path ||
    payload?.explanation?.excel_path; // por si el back lo anida ahí

  if (typeof pathFromRoot === "string" && pathFromRoot.trim()) {
    const p = pathFromRoot.trim();
    // Si parece URL absoluta, úsala tal cual; si no, confía en que el servidor lo sirve como ruta pública
    const isAbsolute = /^https?:\/\//i.test(p);
    return {
      href: isAbsolute ? p : p,
      filename: p.split("/").pop() || "resultado.xlsx",
    };
  }

  return null;
}

function parsePythonishToObject(input) {
  if (!input || typeof input !== "string") return null;

  // si no parece objeto/lista/dict, NO intentes parsear
  const s0 = input.trim();
  const looksLikeObject =
    s0.startsWith("{") ||
    s0.startsWith("[") ||
    // algo tipo clave: valor
    /^[\s'"]?[A-Za-z0-9_]+['"]?\s*:/.test(s0);
  if (!looksLikeObject) return null;

  try {
    let s = s0;

    // Quita comillas exteriores si envuelven todo
    if (
      (s.startsWith('"') && s.endsWith('"')) ||
      (s.startsWith("'") && s.endsWith("'"))
    ) {
      s = s.slice(1, -1);
    }

    // Normaliza secuencias de escape comunes
    s = s
      .replace(/\\n/g, " ")
      .replace(/\\r/g, "")
      .replace(/\\t/g, " ")
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\x[0-9A-Fa-f]{2}/g, " ");

    // Enmascara cadenas dobles para no tocarlas
    const placeholders = [];
    s = s.replace(/"([^"\\]|\\.)*"/g, (m) => {
      const idx = placeholders.length;
      placeholders.push(m);
      return `__DQSTR_${idx}__`;
    });

    // Convierte dict Python a JSON
    s = s
      .replace(/'/g, '"')
      .replace(/\bNone\b/g, "null")
      .replace(/\bTrue\b/g, "true")
      .replace(/\bFalse\b/g, "false");

    // Desenmascara
    s = s.replace(/__DQSTR_(\d+)__/g, (_, i) => placeholders[Number(i)]);

    return JSON.parse(s);
  } catch (e) {
    console.error("parsePythonishToObject failed:", e);
    return null;
  }
}

function toChatMessage(payload) {
  // Excel (acepta top-level y anidado por compatibilidad)
  const excelName =
    payload?.excel?.name ||
    (typeof payload?.excel_name === "string" ? payload.excel_name : null);

  const excelBytes =
    payload?.excel?.b64 ||
    (typeof payload?.excel_bytes === "string" ? payload.excel_bytes : null);

  // Datos tabulares: preferimos 'data', si no 'query_results'
  const d = payload?.data || payload?.query_results || null;

  // Contenido textual
  const content =
    typeof payload?.explanation === "string"
      ? payload.explanation
      : payload?.answer ?? payload?.content ?? "";

  // ui: acciones estructuradas del backend (ej. "abrí el form de crear paciente con estos datos").
  // Se propaga tal cual a todas las ramas de abajo.
  const ui = payload?.ui ?? null;

  // Si hay datos tabulares con columns + results
  if (d?.columns?.length && Array.isArray(d?.results)) {
    return {
      role: "assistant",
      content:
        content ||
        `Se obtuvieron ${d?.row_count ?? d.results.length} registros.`,
      columns: d.columns,
      rows: d.results,
      excelName,
      excelBytes,
      ui,
    };
  }

  // Si hay datos tabulares con rows (sin columns — generamos columnas automáticamente)
  const rawRows = Array.isArray(d?.rows) ? d.rows : null;
  if (rawRows?.length) {
    const columns = Object.keys(rawRows[0]);
    return {
      role: "assistant",
      content: content || `Se obtuvieron ${d?.row_count ?? rawRows.length} registros.`,
      columns,
      rows: rawRows,
      excelName,
      excelBytes,
      ui,
    };
  }

  // Sin tabla, pero quizá hay Excel directo como URL (compat)
  const download =
    extractExcelDownload(payload) || extractExcelDownload(payload?.explanation);

  return {
    role: "assistant",
    content: String(content || ""),
    columns: [],
    rows: [],
    ...(download ? { download } : {}),
    excelName,
    excelBytes,
    ui,
  };
}

/* ===== Thunk: ya devuelve el mensaje listo para empujar al chat ===== */
export const askAi = createAsyncThunk(
  "chat/askAi",
  async (query, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${apiAI}/askai`, {
        query,
        session_id: SESSION_ID,
        doctor_id: doctorId,
        doctor_name: drName,
      });
      return toChatMessage(data);
    } catch (err) {
      // intenta parsear el cuerpo del error para igual mostrar algo útil
      const payload = err?.response?.data ?? err?.message ?? "Error";
      try {
        return rejectWithValue(toChatMessage(payload));
      } catch {
        return rejectWithValue({
          role: "assistant",
          content: `⚠️ ${String(payload)}`,
          columns: [],
          rows: [],
        });
      }
    }
  }
);

/* ===== Slice ===== */
const initialState = {
  status: "idle",
  messages: [], // cada item: { role, content, columns?, rows? }
  error: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearChat(state) {
      state.messages = [];
      state.status = "idle";
      state.error = null;
      resetChatSession(); // nueva sesión → olvida el "último paciente"
    },
    addUserMessage(state, action) {
      state.messages.push({ role: "user", content: action.payload });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(askAi.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(askAi.fulfilled, (state, action) => {
        state.status = "idle";
        // action.payload YA es un mensaje final listo para push
        state.messages.push(action.payload);
      })
      .addCase(askAi.rejected, (state, action) => {
        state.status = "idle";
        // En este flujo, rejectWithValue nos trae un "mensaje" listo si se pudo parsear;
        // si no, armamos uno simple.
        const fallback =
          action.payload && action.payload.role
            ? action.payload
            : {
                role: "assistant",
                content: `⚠️ ${action.error?.message || "Error"}`,
                columns: [],
                rows: [],
              };
        state.messages.push(fallback);
        state.error = action.error?.message || null;
      });
  },
});

export const { clearChat, addUserMessage } = chatSlice.actions;
export default chatSlice.reducer;

// Selectors (asegúrate que en tu store la clave sea "chat")
export const getChatMessages = (s) => s.chat.messages;
export const getChatStatus = (s) => s.chat.status;
export const getChatError = (s) => s.chat.error;
